/**
 * QA Checklist Panel
 * 
 * Displays checklist items grouped by category with completion tracking.
 * Supports both writer and reviewer checklists with rubric scoring.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { CheckCircle, Circle, AlertCircle, Star } from 'lucide-react';
import { 
  getQAChecklist, 
  updateChecklistItem,
  batchUpdateChecklist,
  type QAChecklistResponse,
  type ChecklistItemUpdate
} from '../../api/qaApi';

export interface QAChecklistPanelProps {
  reportId: string;
  checklistType: 'writer' | 'reviewer';
  onCompletionChange?: (completed: boolean, percentage: number) => void;
  isAdmin?: boolean;
}

export function QAChecklistPanel({
  reportId,
  checklistType,
  onCompletionChange,
  isAdmin = false
}: QAChecklistPanelProps) {
  const queryClient = useQueryClient();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  
  const { data, isLoading, error } = useQuery({
    queryKey: ['qa-checklist', reportId, checklistType],
    queryFn: () => getQAChecklist(reportId, checklistType),
    refetchOnWindowFocus: false
  });
  
  const updateMutation = useMutation({
    mutationFn: (update: ChecklistItemUpdate) =>
      updateChecklistItem(reportId, checklistType, update),
    onSuccess: (newStatus) => {
      // Invalidate the checklist query
      queryClient.invalidateQueries({ queryKey: ['qa-checklist', reportId, checklistType] });
      
      // Invalidate report queries to update completion counts in UI
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      queryClient.invalidateQueries({ queryKey: ['report-review', reportId] });
      
      if (onCompletionChange) {
        onCompletionChange(newStatus.all_completed, newStatus.completion_percentage);
      }
    },
    onError: (error: any) => {
      console.error('[QA Checklist] Failed to update item:', error);
      alert(`Failed to update checklist item: ${error.message || 'Unknown error'}`);
    }
  });
  
  const batchUpdateMutation = useMutation({
    mutationFn: (request: { completed: boolean; default_rubric_score?: number }) =>
      batchUpdateChecklist(reportId, checklistType, request),
    onSuccess: (response) => {
      // Invalidate the checklist query
      queryClient.invalidateQueries({ queryKey: ['qa-checklist', reportId, checklistType] });
      
      // Invalidate report queries that contain checklist completion data
      // This ensures counts/percentages update in report cards and lists
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      queryClient.invalidateQueries({ queryKey: ['reports'] }); // List views
      queryClient.invalidateQueries({ queryKey: ['report-review', reportId] }); // Review assignments
      
      if (onCompletionChange) {
        onCompletionChange(
          response.status.all_completed,
          response.status.completion_percentage
        );
      }
      console.log(`[QA Checklist] Batch updated ${response.updated_count} items`);
    },
    onError: (error: any) => {
      console.error('[QA Checklist] Failed to batch update:', error);
      alert(`Failed to batch update checklist: ${error.message || 'Unknown error'}`);
    }
  });
  
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };
  
  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading checklist...</span>
        </div>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card>
        <div className="text-center py-8">
          <AlertCircle className="h-12 w-12 mx-auto text-red-400 mb-4" />
          <p className="text-red-600 dark:text-red-400">
            Failed to load checklist: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      </Card>
    );
  }
  
  if (!data) {
    return (
      <Card>
        <div className="text-center py-8">
          <p className="text-gray-600 dark:text-gray-400">No checklist data available</p>
        </div>
      </Card>
    );
  }
  
  const { config, status } = data;
  
  return (
    <Card>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold">
            {checklistType === 'writer' ? 'Writer' : 'Reviewer'} Checklist
          </h3>
          <div className="flex items-center gap-2">
            <Badge 
              variant={status.all_completed ? 'success' : status.completion_percentage >= 50 ? 'warning' : 'error'}
              className="text-sm"
            >
              {status.completion_percentage.toFixed(0)}% Complete
            </Badge>
            {/* Bulk action buttons - show to all users, not just admins */}
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="primary"
                disabled={batchUpdateMutation.isPending}
                onClick={() => {
                  // Mark all as complete
                  // For reviewer checklists, set default rubric score to 5/5
                  batchUpdateMutation.mutate({
                    completed: true,
                    default_rubric_score: checklistType === 'reviewer' ? 5 : undefined
                  });
                }}
                className="text-xs"
                title="Mark all checklist items as complete (reviewer items get 5/5 score)"
              >
                ✓ All
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={batchUpdateMutation.isPending}
                onClick={() => {
                  // Mark all as incomplete
                  batchUpdateMutation.mutate({
                    completed: false
                  });
                }}
                className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30"
                title="Mark all checklist items as incomplete"
              >
                ✗ All
              </Button>
            </div>
          </div>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {status.completed_items} of {status.total_items} items completed
        </div>
        {status.all_completed && (
          <div className="mt-2 text-sm text-green-600 dark:text-green-400 font-medium">
            ✓ All checklist items completed
          </div>
        )}
      </div>
      
      <div className="space-y-4">
        {config.categories.map((category) => {
          const isExpanded = expandedCategories.has(category.id);
          const categoryItems = status.checklist_data?.items?.[category.id] || {};
          const categoryCompleted = category.items.filter(item => 
            categoryItems[item.id]?.completed
          ).length;
          const categoryTotal = category.items.length;
          const categoryPercentage = categoryTotal > 0 ? (categoryCompleted / categoryTotal * 100) : 0;
          
          return (
            <div 
              key={category.id} 
              className="border border-theme-border rounded-lg overflow-hidden"
            >
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full px-4 py-3 bg-theme-surface hover:bg-theme-surface-hover transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <span className="font-medium text-theme-text">{category.name}</span>
                  <Badge variant={categoryPercentage === 100 ? 'success' : 'default'} size="sm">
                    {categoryCompleted}/{categoryTotal}
                  </Badge>
                </div>
                <span className="text-gray-500 dark:text-gray-400">
                  {isExpanded ? '▼' : '▶'}
                </span>
              </button>
              
              {isExpanded && (
                <div className="p-4 space-y-3 bg-theme-paper">
                  {category.items.map((item) => {
                    const itemStatus = categoryItems[item.id] || {};
                    const isCompleted = itemStatus.completed || false;
                    
                    return (
                      <div
                        key={item.id}
                        className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                          isCompleted 
                            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                            : 'bg-theme-surface hover:bg-theme-surface-hover border border-theme-border'
                        }`}
                        onClick={(e) => {
                          // Allow clicking anywhere on the item to toggle
                          const target = e.target as HTMLElement;
                          if (target.closest('button') || target.closest('input') || target.closest('textarea')) {
                            return; // Let button handle its own click
                          }
                          // Toggle on item click
                          if (!updateMutation.isPending) {
                            updateMutation.mutate({
                              category_id: category.id,
                              item_id: item.id,
                              completed: !isCompleted
                            });
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!updateMutation.isPending) {
                              updateMutation.mutate({
                                category_id: category.id,
                                item_id: item.id,
                                completed: !isCompleted
                              });
                            }
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (!updateMutation.isPending) {
                              updateMutation.mutate({
                                category_id: category.id,
                                item_id: item.id,
                                completed: !isCompleted
                              });
                            }
                          }}
                          className="mt-0.5 flex-shrink-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded"
                          disabled={updateMutation.isPending}
                          aria-label={isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
                          type="button"
                        >
                          {isCompleted ? (
                            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                          ) : (
                            <Circle className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={isCompleted ? 'line-through text-gray-500 dark:text-gray-400' : 'text-theme-text font-medium'}>
                              {item.text}
                            </span>
                            {item.mandatory && (
                              <Badge variant="error" size="sm" className="text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">Required</Badge>
                            )}
                            {item.rubric_score && (
                              <Badge variant="info" size="sm" className="text-blue-500 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">Rubric</Badge>
                            )}
                          </div>
                          {item.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {item.description}
                            </p>
                          )}
                          {item.rubric_score && isCompleted && (
                            <div className="mt-3 flex items-center gap-2">
                              <label className="text-sm font-medium text-theme-text">
                                Approval Score (1-5):
                              </label>
                              <div className="flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map((score) => {
                                  const isFilled = itemStatus.approval_score != null && score <= itemStatus.approval_score;
                                  const isSelected = itemStatus.approval_score === score;
                                  return (
                                    <button
                                      key={score}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        updateMutation.mutate({
                                          category_id: category.id,
                                          item_id: item.id,
                                          completed: true,
                                          rubric_score: score
                                        });
                                      }}
                                      className={`p-1 rounded transition-colors ${
                                        isSelected
                                          ? 'bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400'
                                          : 'text-blue-600 hover:text-primary-500 dark:hover:text-primary-400'
                                      }`}
                                      disabled={updateMutation.isPending}
                                      aria-label={`Rate ${score} out of 5`}
                                    >
                                      <Star 
                                        className={`w-5 h-5 ${
                                          isFilled ? 'fill-current' : ''
                                        }`}
                                      />
                                    </button>
                                  );
                                })}
                              </div>
                              {itemStatus.approval_score && (
                                <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">
                                  ({itemStatus.approval_score}/5)
                                </span>
                              )}
                            </div>
                          )}
                          {itemStatus.notes && (
                            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 italic">
                              Notes: {itemStatus.notes}
                            </div>
                          )}
                          {itemStatus.reviewer_notes && (
                            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 italic">
                              Reviewer Notes: {itemStatus.reviewer_notes}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {status.all_completed && checklistType === 'reviewer' && status.checklist_data?.overall_score && (
        <div className="mt-6 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
          <div className="flex items-center justify-between">
            <span className="font-medium text-theme-text">Overall Approval Score:</span>
            <Badge variant="success" className="text-lg px-3 py-1">
              {status.checklist_data.overall_score.toFixed(1)}/5.0
            </Badge>
          </div>
        </div>
      )}
    </Card>
  );
}
